import {
  estimateFeesPerGas,
  getAccount,
  getBalance,
  getGasPrice,
  getPublicClient,
  readContract,
  simulateContract,
  switchChain,
  waitForTransactionReceipt,
  writeContract,
} from "@wagmi/core";
import { Big } from "big.js";
import { t } from "i18next";
import { Address, encodeAbiParameters, formatEther, formatUnits, parseAbiParameters } from "viem";

import { ToastService } from "@/components/ToastService";
import { TOAST_ID } from "@/constants";
import { CHAIN_ID } from "@/constants/chains";

import { hapiApi } from "../api/hapi";
import { config } from "../config";
import { CHAIN_INFO, wagmiConfig } from "../config/evm";
import { hapiEVMModuleAbi, hapiEVMPortalAbi } from "../contracts/evm/abi";
import { hapiEVMAbiNative } from "../contracts/evm/abiNative";

type MintScoreParams = {
  address: string;
  chainId: CHAIN_ID;
  trust: number;
  expirationDate: number;
  validation: string;
  isReMint: boolean;
  attestationId?: string | null;
};

const mintScore = async ({
  address,
  chainId,
  trust,
  expirationDate,
  validation,
  isReMint,
  attestationId,
}: MintScoreParams) => {
  const canRevokeAttestation = Boolean(attestationId);

  const name = isReMint ? "re-mint" : "mint";
  const icon = CHAIN_INFO[chainId].icon;

  const { portalId, schemaId, moduleId } = config.evm.attestation[chainId];

  try {
    const account = getAccount(wagmiConfig);

    if (chainId !== account.chainId) {
      const chain = await switchChain(wagmiConfig, { chainId });
      if (chain.id !== chainId) throw Error("Chain doesn't support");
    }

    const attestationFee = await readContract(
      wagmiConfig,
      isReMint
        ? {
            abi: hapiEVMModuleAbi,
            address: moduleId,
            functionName: "updateAttestationFee",
          }
        : {
            abi: hapiEVMModuleAbi,
            address: moduleId,
            functionName: "createAttestationFee",
          },
    );

    ToastService.loading({ text: t("Messages.LoadingAction", { name }), id: TOAST_ID.MINT });
    const balance = await getBalance(wagmiConfig, { address: address as Address });

    if (Big(balance.value.toString()).lt(Number(attestationFee))) {
      ToastService.update({
        text: `Insufficient Balance - ${formatUnits(attestationFee, balance.decimals)} ${balance.symbol}`,
        id: TOAST_ID.MINT,
        icon,
      });
      return false;
    }

    const attestationPayload = {
      schemaId,
      expirationDate: BigInt(expirationDate),
      subject: address as Address,
      attestationData: encodeAbiParameters(parseAbiParameters(["uint16", "uint64", "bool"]), [
        trust,
        BigInt(chainId),
        isReMint,
      ]),
    };

    // Estimate gas and fees through the app's own (working) RPC so the transaction
    // does not depend on the wallet's gas oracle. When that oracle is unavailable
    // the wallet underprices the tx and the node rejects it ("intrinsic gas too low").
    // If our estimation fails too, fall back to the wallet's own handling (previous behaviour).
    const gasOverrides: { gas?: bigint; maxFeePerGas?: bigint; maxPriorityFeePerGas?: bigint } = {};
    try {
      const publicClient = getPublicClient(wagmiConfig, { chainId });
      if (publicClient) {
        const estimatedGas = canRevokeAttestation
          ? await publicClient.estimateContractGas({
              address: portalId,
              abi: hapiEVMPortalAbi,
              functionName: "replace",
              args: [attestationId as Address, attestationPayload, [validation as Address]],
              value: attestationFee,
              account: address as Address,
            })
          : await publicClient.estimateContractGas({
              address: portalId,
              abi: hapiEVMPortalAbi,
              functionName: "attest",
              args: [attestationPayload, [validation as Address]],
              value: attestationFee,
              account: address as Address,
            });
        const fees = await estimateFeesPerGas(wagmiConfig, { chainId });
        gasOverrides.gas = (estimatedGas * 125n) / 100n; // +25% buffer
        gasOverrides.maxFeePerGas = fees.maxFeePerGas;
        gasOverrides.maxPriorityFeePerGas = fees.maxPriorityFeePerGas;
      }
    } catch (estimationError) {
      console.warn("EVM gas estimation failed, falling back to wallet defaults", estimationError);
    }

    const { request } = await simulateContract(
      wagmiConfig,
      canRevokeAttestation
        ? {
            address: portalId,
            abi: hapiEVMPortalAbi,
            functionName: "replace",
            args: [attestationId as Address, attestationPayload, [validation as Address]],
            value: attestationFee,
            ...gasOverrides,
          }
        : {
            address: portalId,
            abi: hapiEVMPortalAbi,
            functionName: "attest",
            args: [attestationPayload, [validation as Address]],
            value: attestationFee,
            ...gasOverrides,
          },
    );
    const hash = await writeContract(wagmiConfig, request);
    console.log("hash", hash);
    const { status } = await waitForTransactionReceipt(wagmiConfig, { hash });
    console.log("status", status);
    if (status === "reverted") throw Error(isReMint ? "Attestation revocation error" : "Attestation assignment error");

    await hapiApi.logAttest({ chain: chainId, address, method: isReMint ? 1 : 0 }); //method - 0 attest | method - 1 replace

    ToastService.update({ text: `Trust score is ${name}ed`, id: TOAST_ID.MINT, icon });
    return true;
  } catch (error) {
    console.error(`Error, while ${name} trust score on EVM \n\n`, error);
    if (error instanceof Error) {
      console.error({ name: error.name, message: error.message, stack: error.stack });
      ToastService.update({ text: error.name, id: TOAST_ID.MINT, icon });
    }
    return false;
  }
};

const mintScoreNative = async ({ address, chainId, trust, validation, isReMint }: MintScoreParams) => {
  const name = isReMint ? "re-mint" : "mint";
  const icon = CHAIN_INFO[chainId].icon;

  const { contractId } = config.evm.native[chainId];
  const gasPrice = await getGasPrice(wagmiConfig);
  const gasPriceEth = formatEther(gasPrice, "wei");
  const gasLimit = 110000;

  try {
    const account = getAccount(wagmiConfig);
    if (chainId !== account.chainId) {
      const chain = await switchChain(wagmiConfig, { chainId });
      if (chain.id !== chainId) throw Error("Chain doesn't support");
    }

    const attestationFee = await readContract(
      wagmiConfig,
      isReMint
        ? {
            abi: hapiEVMAbiNative,
            address: contractId,
            functionName: "remintCost",
          }
        : {
            abi: hapiEVMAbiNative,
            address: contractId,
            functionName: "mintCost",
          },
    );

    ToastService.loading({ text: t("Messages.LoadingAction", { name }), id: TOAST_ID.MINT });
    const balance = await getBalance(wagmiConfig, { address: address as Address });

    if (Big(balance.value.toString()).add(Big(gasPriceEth).mul(gasLimit).toString()).lt(Number(attestationFee))) {
      ToastService.update({
        text: `Insufficient Balance - ${formatUnits(attestationFee as bigint, balance.decimals)} ${balance.symbol}`,
        id: TOAST_ID.MINT,
        icon,
      });
      return false;
    }

    const attestationPayload = encodeAbiParameters(parseAbiParameters(["address", "uint16", "uint64"]), [
      address as Address,
      trust,
      BigInt(chainId),
    ]);

    // Estimate gas and fees through the app's own (working) RPC so the transaction
    // does not depend on the wallet's gas oracle. When that oracle is unavailable
    // the wallet underprices the tx and the node rejects it ("intrinsic gas too low").
    // If our estimation fails too, fall back to the wallet's own handling (previous behaviour).
    const gasOverrides: { gas?: bigint; maxFeePerGas?: bigint; maxPriorityFeePerGas?: bigint } = {};
    try {
      const publicClient = getPublicClient(wagmiConfig, { chainId });
      if (publicClient) {
        const estimatedGas = await publicClient.estimateContractGas({
          address: contractId,
          abi: hapiEVMAbiNative,
          functionName: "selfAttest",
          args: [validation, attestationPayload],
          value: attestationFee as bigint,
          account: address as Address,
        });
        const fees = await estimateFeesPerGas(wagmiConfig, { chainId });
        gasOverrides.gas = (estimatedGas * 125n) / 100n; // +25% buffer
        gasOverrides.maxFeePerGas = fees.maxFeePerGas;
        gasOverrides.maxPriorityFeePerGas = fees.maxPriorityFeePerGas;
      }
    } catch (estimationError) {
      console.warn("EVM gas estimation failed, falling back to wallet defaults", estimationError);
    }

    const { request } = await simulateContract(wagmiConfig, {
      address: contractId,
      abi: hapiEVMAbiNative,
      functionName: "selfAttest",
      args: [validation, attestationPayload],
      value: attestationFee as bigint,
      ...gasOverrides,
    });
    const hash = await writeContract(wagmiConfig, request);
    const { status } = await waitForTransactionReceipt(wagmiConfig, { hash });
    if (status === "reverted") throw Error(isReMint ? "Attestation revocation error" : "Attestation assignment error");

    await hapiApi.logAttest({ chain: chainId, address, method: isReMint ? 1 : 0 }); //method - 0 attest | method - 1 replace

    ToastService.update({ text: `Trust score is ${name}ed`, id: TOAST_ID.MINT, icon });
    return true;
  } catch (error) {
    console.error(`Error, while ${name} trust score on EVM \n\n`, error);
    if (error instanceof Error) {
      console.error({ name: error.name, message: error.message, stack: error.stack });
      ToastService.update({ text: error.name, id: TOAST_ID.MINT, icon });
    }
    return false;
  }
};

const mintNft = async () => {};

export default {
  mintNft,
  mintScore,
  mintScoreNative,
};
